import { FunctionComponent } from "react";

import { faTrash, faCheck, faXmark, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { InputField } from "@/components/InputField/InputField";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import {
	PipedriveFieldMapping,
	PipedriveField,
	PipedriveSourceField,
	PipedriveEntityType,
	FieldMappingSourceType,
	FieldMappingAiConfig,
} from "../schema/pipedrive";
import {
	AiHintBanner,
	AiSourceCell,
	CannotMapRow,
	SourceTypePills,
	TargetFieldBadges,
} from "./fieldMapping";

type Ts = (key: string, options?: Record<string, unknown>) => string;

const buildBadgeLabels = (ts: Ts) => ({
	required: ts("metaBadge.required"),
	multi: ts("metaBadge.multi"),
	dropdown: ts("metaBadge.dropdown"),
	number: ts("metaBadge.number"),
	calculated: ts("metaBadge.calculated"),
	readOnly: ts("metaBadge.readOnly"),
});

const isFieldUnwritable = (field: PipedriveField | undefined): boolean =>
	Boolean(field?.isReadOnly) || Boolean(field?.isCalculated);

/**
 * A Pipedrive lead only takes custom fields as mapping targets, so the generic "no results"
 * leaves the user hunting for an email field that lives on another tab (PREC-4078).
 */
const buildTargetNoResultsText = (entityType: PipedriveEntityType, ts: Ts): string | undefined =>
	entityType === PipedriveEntityType.LEAD ? ts("leadTargetNoResults") : undefined;

interface MappingRowProps {
	mapping: PipedriveFieldMapping;
	sourceFields: PipedriveSourceField[];
	pipedriveFields: PipedriveField[];
	onToggleActive: (id: number, isActive: boolean) => void;
	onDelete: (id: number) => void;
	onConfigureAi: (mapping: PipedriveFieldMapping) => void;
	ts: Ts;
}

export const PipedriveMappingRow: FunctionComponent<MappingRowProps> = ({
	mapping,
	sourceFields,
	pipedriveFields,
	onToggleActive,
	onDelete,
	onConfigureAi,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const pipedriveField = pipedriveFields.find((f) => f.name === mapping.pipedriveField);
	const pipedriveLabel = pipedriveField
		? `${pipedriveField.label}${pipedriveField.isCustom ? " (Custom)" : ""}`
		: mapping.pipedriveField;
	const badgeLabels = buildBadgeLabels(ts);

	// A stored LEAD mapping whose target Pipedrive no longer offers for leads is dead: the sync
	// skips it, so it has to read as unmappable instead of as a working row (PREC-4078).
	const isDroppedLeadTarget =
		mapping.entityType === PipedriveEntityType.LEAD &&
		pipedriveFields.length > 0 &&
		!pipedriveField;

	if (isFieldUnwritable(pipedriveField) || isDroppedLeadTarget) {
		return (
			<CannotMapRow
				badgeLabels={badgeLabels}
				cannotMapLabel={ts("cannotMap")}
				targetField={pipedriveField || {}}
				targetFieldLabel={pipedriveLabel}
				testIdPrefix="pipedrive"
				tooltip={isDroppedLeadTarget ? ts("leadCannotMapTooltip") : ts("cannotMapTooltip")}
				onDelete={() => onDelete(mapping.id)}
			/>
		);
	}

	const sourceLabel = isConstant
		? ts("constantLabel", { value: mapping.sourceField })
		: sourceFields.find((f) => f.name === mapping.sourceField)?.label || mapping.sourceField;

	return (
		<div className="flex items-center gap-3 py-3 border-b border-border-light">
			<div className="flex-1">
				{isAi ? (
					<AiSourceCell
						badgeLabel={ts("aiBadge")}
						configureLabel={ts("aiConfigure")}
						creditTooltip={ts("aiCreditTooltip")}
						label={pipedriveLabel}
						testIdPrefix="pipedrive"
						onConfigure={() => onConfigureAi(mapping)}
					/>
				) : (
					<div
						className={classNames(
							"py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-brand-white text-text",
							isConstant && "italic text-text-meta"
						)}
						data-testid={isConstant ? "pipedrive-constant-label" : "pipedrive-field-label"}
					>
						{sourceLabel}
					</div>
				)}
			</div>
			<div className="w-[24px] flex items-center justify-center">
				<Icon className="w-[16px] h-[16px] text-text-meta" icon={faArrowRight} />
			</div>
			<div className="flex-1">
				<div className="py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-brand-white text-text flex items-center gap-2">
					<span className="flex-1 truncate">{pipedriveLabel}</span>
					<TargetFieldBadges
						field={pipedriveField || {}}
						labels={badgeLabels}
						testIdPrefix="pipedrive"
					/>
				</div>
			</div>
			<div className="w-[60px] flex items-center justify-center">
				<Toggle
					isChecked={mapping.isActive}
					onChange={() => onToggleActive(mapping.id, !mapping.isActive)}
				/>
			</div>
			<div
				className="w-fit bg-status-error-light flex justify-end p-1 rounded border-border-light group hover:border-text-status-error cursor-pointer"
				onClick={() => onDelete(mapping.id)}
			>
				<Icon className="text-status-error cursor-pointer w-4 h-4" icon={faTrash} />
			</div>
		</div>
	);
};

export interface NewPipedriveMapping {
	tempId: string;
	sourceField: string;
	sourceType: FieldMappingSourceType;
	aiConfig?: FieldMappingAiConfig;
	pipedriveField: string;
	isActive: boolean;
	entityType: PipedriveEntityType;
}

interface NewMappingRowProps {
	mapping: NewPipedriveMapping;
	sourceFields: PipedriveSourceField[];
	pipedriveFields: PipedriveField[];
	usedSourceFields: string[];
	usedPipedriveFieldsByType: Record<FieldMappingSourceType, string[]>;
	onUpdate: (tempId: string, data: Partial<NewPipedriveMapping>) => void;
	onSave: (mapping: NewPipedriveMapping) => void;
	onCancel: (tempId: string) => void;
	onConfigureAi: (mapping: NewPipedriveMapping) => void;
	isSaving: boolean;
	ts: Ts;
}

export const PipedriveNewMappingRow: FunctionComponent<NewMappingRowProps> = ({
	mapping,
	sourceFields,
	pipedriveFields,
	usedSourceFields,
	usedPipedriveFieldsByType,
	onUpdate,
	onSave,
	onCancel,
	onConfigureAi,
	isSaving,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const usedPipedriveFields = usedPipedriveFieldsByType[mapping.sourceType] ?? [];

	const availableSourceFields = sourceFields.filter(
		(f) => f.name === mapping.sourceField || !usedSourceFields.includes(f.name)
	);
	const availablePipedriveFields = pipedriveFields.filter(
		(f) =>
			(f.name === mapping.pipedriveField || !usedPipedriveFields.includes(f.name)) &&
			!isFieldUnwritable(f)
	);

	const sourceOptions: AutoCompleteItem[] = availableSourceFields.map((f) => ({
		id: f.name,
		title: f.label,
	}));

	const pipedriveOptions: AutoCompleteItem[] = availablePipedriveFields.map((f) => ({
		id: f.name,
		title: `${f.label}${f.isCustom ? " (Custom)" : ""}`,
	}));

	const selectedSource = sourceOptions.find((o) => o.id === mapping.sourceField);
	const selectedPipedrive = pipedriveOptions.find((o) => o.id === mapping.pipedriveField);
	const selectedPipedriveField = pipedriveFields.find((f) => f.name === mapping.pipedriveField);
	const canSave = isAi
		? Boolean(mapping.pipedriveField && mapping.aiConfig?.prompt)
		: Boolean(mapping.sourceField) && Boolean(mapping.pipedriveField);
	const saveDisabledTooltip = isAi && !mapping.aiConfig?.prompt ? ts("aiSaveDisabledTooltip") : "";
	const badgeLabels = buildBadgeLabels(ts);

	const handleSourceTypeChange = (next: FieldMappingSourceType) => {
		if (next === mapping.sourceType) {
			return;
		}

		onUpdate(mapping.tempId, { sourceType: next, sourceField: "", aiConfig: undefined });
	};

	return (
		<div className="flex flex-col gap-2 py-3 border-b border-border-light bg-brand-white rounded-lg px-2">
			<div className="flex items-start gap-3">
				<div className="flex-1 flex flex-col gap-2">
					<SourceTypePills
						labels={{
							fromRecord: ts("sourceTypeFromRecord"),
							constant: ts("sourceTypeConstant"),
							ai: ts("sourceTypeAi"),
						}}
						testIdPrefix="pipedrive"
						value={mapping.sourceType}
						onChange={handleSourceTypeChange}
					/>
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureDisabled={!mapping.pipedriveField}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={selectedPipedrive?.title || ""}
							placeholder={ts("aiTargetPlaceholder")}
							testIdPrefix="pipedrive"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : isConstant ? (
						<InputField
							maxLength={255}
							name={`pipedrive-constant-${mapping.tempId}`}
							placeholder={ts("constantPlaceholder")}
							testId="pipedrive-constant-input"
							value={mapping.sourceField}
							handleChange={(e) => onUpdate(mapping.tempId, { sourceField: e.target.value })}
						/>
					) : (
						<AutoComplete
							data={sourceOptions}
							handleReset={() => onUpdate(mapping.tempId, { sourceField: "" })}
							handleSearch={() => {}}
							handleSelect={(item) => {
								if (item) {
									onUpdate(mapping.tempId, { sourceField: item.id as string });
								}
							}}
							placeholder={ts("selectField")}
							selectedItem={selectedSource?.title || ""}
						/>
					)}
				</div>
				<div className="w-[24px] flex items-center justify-center pt-9">
					<Icon className="w-[16px] h-[16px] text-text-meta" icon={faArrowRight} />
				</div>
				<div className="flex-1 pt-9 flex flex-col gap-1">
					<AutoComplete
						data={pipedriveOptions}
						handleReset={() =>
							onUpdate(mapping.tempId, {
								pipedriveField: "",
								sourceField: isAi ? "" : mapping.sourceField,
							})
						}
						handleSearch={() => {}}
						handleSelect={(item) => {
							if (item) {
								onUpdate(mapping.tempId, {
									pipedriveField: item.id as string,
									sourceField: isAi ? item.title : mapping.sourceField,
								});
							}
						}}
						noResultsText={buildTargetNoResultsText(mapping.entityType, ts)}
						placeholder={ts("selectField")}
						selectedItem={selectedPipedrive?.title || ""}
					/>
					{selectedPipedriveField && (
						<TargetFieldBadges
							field={selectedPipedriveField}
							labels={badgeLabels}
							testIdPrefix="pipedrive-new"
						/>
					)}
				</div>
				<div className="w-[60px] flex items-center justify-center pt-9">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onUpdate(mapping.tempId, { isActive: !mapping.isActive })}
					/>
				</div>
				<div className="w-[80px] flex gap-2 pt-9">
					<Tooltip content={saveDisabledTooltip} disabled={!saveDisabledTooltip}>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								disabled={!canSave}
								image={
									<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-white" icon={faCheck} />
								}
								isLoading={isSaving}
								size={ButtonSize.XS}
								testId="pipedrive-save-new-mapping"
								title=""
								onClick={() => onSave(mapping)}
							/>
						</div>
					</Tooltip>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-text" icon={faXmark} />}
							size={ButtonSize.XS}
							title=""
							onClick={() => onCancel(mapping.tempId)}
						/>
					</div>
				</div>
			</div>
			{isAi && (
				<AiHintBanner body={ts("aiHintBody")} testIdPrefix="pipedrive" title={ts("aiHintTitle")} />
			)}
		</div>
	);
};
